import { useMemo, useState } from "react";
import { BlockLink } from "@/components/DrilldownLink";
import HeroStatusChip from "@/components/HeroStatusChip";
import InfrastructureWeakSchoolsModal from "@/components/InfrastructureWeakSchoolsModal";
import InfrastructureKpiSchoolsModal from "@/components/InfrastructureKpiSchoolsModal";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
const pct = (v) => (typeof v === "number" ? `${v}%` : "N/A");

const DONUT_VARIANT = {
  "Classroom Health": "",
  "Toilet Functional": "",
  "Water Availability": "blue",
  Electricity: "purple",
};

const blockInfraIndex = (block, districtWater, districtElectricity) => {
  const classroomHealth = block.classroom_health ?? 0;
  const toiletPct = block.toilet_pct ?? 0;
  const water = block.water_coverage_pct ?? districtWater;
  const electricity = block.electricity_pct ?? districtElectricity;
  return Math.round((classroomHealth * 0.3 + toiletPct * 0.25 + water * 0.25 + electricity * 0.2) * 10) / 10;
};

const priorityTag = (index) => {
  if (index >= 92) return { label: "Good", className: "green" };
  if (index >= 88) return { label: "Medium", className: "" };
  return { label: "High", className: "red" };
};

const MetricDonut = ({ value, display, label, subtitle, variant = "" }) => (
  <div className="ed-infra-metric-donut">
    <div className={`ed-infra-donut ${variant}`} style={{ "--pct": `${Math.min(Math.max(value, 0), 100)}%` }}>
      <strong>{display ?? `${value}%`}</strong>
    </div>
    <b>{label}</b>
    <span>{subtitle}</span>
  </div>
);

const KpiCard = ({ label, value, hint, iconVariant, children, onClick, clickable = false }) => (
  <article
    className={`ed-infra-kpi-card${clickable ? " clickable" : ""}`}
    onClick={clickable ? onClick : undefined}
    onKeyDown={
      clickable
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick?.();
            }
          }
        : undefined
    }
    role={clickable ? "button" : undefined}
    tabIndex={clickable ? 0 : undefined}
    aria-label={clickable ? `${label}: ${value}. ${hint}. Click for details.` : undefined}
  >
    <div className="ed-infra-kpi-top">
      <small>{label}</small>
      <span className={`ed-infra-mini-icon ${iconVariant || ""}`}>{children}</span>
    </div>
    <strong>{value}</strong>
    <span>{hint}</span>
  </article>
);

const ExecutiveInfrastructureTab = ({ infrastructureData, scopeLabel, scopeSchoolName }) => {
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [weakModalOpen, setWeakModalOpen] = useState(false);
  const [kpiModalMetric, setKpiModalMetric] = useState(null);

  const summary = infrastructureData?.summary ?? {};
  const classroom = infrastructureData?.classroom_metrics ?? {};
  const toilet = infrastructureData?.toilet_metrics ?? {};
  const facility = infrastructureData?.facility_metrics ?? {};
  const waterSafety = infrastructureData?.water_safety ?? {};
  const breakdown = infrastructureData?.index_breakdown ?? [];
  const blocks = infrastructureData?.block_performance ?? [];

  const isSchoolScope = (summary.total_schools ?? 0) === 1;
  const infraIndex = summary.infrastructure_index ?? 0;
  const classroomHealth = classroom.classroom_health_pct ?? 0;
  const toiletFunctional = toilet.functional_pct ?? 0;
  const waterCoverage = toilet.water_coverage_pct ?? 0;
  const electricityPct = facility.electricity_pct ?? 0;
  const rainwaterPct = waterSafety.rainwater_harvest_pct ?? 0;
  const purifiedPct = waterSafety.purified_water_pct ?? 0;

  const waterMetrics = useMemo(
    () => [
      { label: "Tap Water", value: waterSafety.tap_water_pct ?? 0 },
      { label: "Purified", value: waterSafety.purified_water_pct ?? 0 },
      { label: "Tested", value: waterSafety.water_tested_pct ?? 0 },
      { label: "Rainwater", value: rainwaterPct },
    ],
    [waterSafety, rainwaterPct],
  );

  const weakBlocks = useMemo(
    () =>
      [...blocks]
        .map((b) => ({ ...b, infra_index: blockInfraIndex(b, waterCoverage, electricityPct) }))
        .sort((a, b) => a.infra_index - b.infra_index)
        .slice(0, 3),
    [blocks, waterCoverage, electricityPct],
  );

  const weakBlockCount = useMemo(
    () => blocks.filter((b) => blockInfraIndex(b, waterCoverage, electricityPct) < 92).length,
    [blocks, waterCoverage, electricityPct],
  );

  const tableRows = useMemo(() => {
    const rows = blocks.map((block) => {
      const idx = blockInfraIndex(block, waterCoverage, electricityPct);
      const tag = priorityTag(idx);
      return {
        ...block,
        water_coverage_pct: block.water_coverage_pct ?? waterCoverage,
        electricity_pct: block.electricity_pct ?? electricityPct,
        infra_index: idx,
        priority: tag,
      };
    });
    if (priorityOnly) {
      return rows.filter((r) => r.priority.className === "red");
    }
    return rows.sort((a, b) => b.infra_index - a.infra_index);
  }, [blocks, waterCoverage, electricityPct, priorityOnly]);

  const weakBlockNames = weakBlocks.map((b) => b.block_name).filter(Boolean).join(", ") || "priority blocks";

  const heroNarrative =
    infraIndex >= 90 && rainwaterPct < 40
      ? "Physical infrastructure is strong overall, but water source reliability and a few weak infrastructure blocks need targeted remediation."
      : infraIndex >= 85
        ? "Infrastructure readiness is stable across classrooms and toilets with selective water-safety follow-up required."
        : "Infrastructure compliance needs block-level intervention to lift classroom health and water availability.";

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const maxWaterBar = 255;

  return (
    <div className="ed-content ed-infra-view" data-testid="executive-infrastructure-tab">
      {isSchoolScope && (
        <p className="ed-scope-note">
          Showing single-school infrastructure metrics{scopeSchoolName ? ` for ${scopeSchoolName}` : ""}. Clear the school filter in the scope bar to view district totals.
        </p>
      )}
      <section className="ed-hero-card ed-infra-hero">
        <div className="ed-infra-layout">
          <div className="ed-infra-summary">
            <div
              className="ed-infra-gauge"
              aria-label={`Infrastructure overview ${infraIndex}`}
              style={{
                background: `radial-gradient(circle at center, white 0 53%, transparent 54%), conic-gradient(var(--ed-accent) 0 ${infraIndex}%, rgba(20,184,166,.11) ${infraIndex}% 100%)`,
              }}
            >
              <div>
                <strong>{infraIndex}</strong>
                <span>Overview</span>
              </div>
            </div>
            <div className="ed-health-copy">
              <h2>Infrastructure Overview</h2>
              <p>{heroNarrative}</p>
              <div className="ed-status-row">
                {classroomHealth >= 90 && (
                  <HeroStatusChip chipClass="ed-status-chip" variant="green">Strong Classroom Health</HeroStatusChip>
                )}
                {toiletFunctional >= 95 && (
                  <HeroStatusChip chipClass="ed-status-chip" variant="green">Toilets Functional</HeroStatusChip>
                )}
                <HeroStatusChip
                  chipClass="ed-status-chip"
                  variant="amber"
                  onClick={() => setKpiModalMetric("water_coverage")}
                  ariaLabel={`Rainwater coverage ${pct(rainwaterPct)} — view schools below water target`}
                >
                  Rainwater: {pct(rainwaterPct)}
                </HeroStatusChip>
              </div>
            </div>
          </div>

          <div className="ed-infra-kpi-grid" aria-label="Infrastructure KPI cards">
            <KpiCard label="Total Classrooms" value={fmt(classroom.total_classrooms)} hint="Available capacity">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-4" />
              </svg>
            </KpiCard>
            <KpiCard
              label="Classroom Health"
              value={pct(classroomHealth)}
              hint="Healthy condition"
              iconVariant="green"
              clickable
              onClick={() => setKpiModalMetric("classroom_health")}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </KpiCard>
            <KpiCard
              label="Toilet Functional"
              value={pct(toiletFunctional)}
              hint="Functional units"
              iconVariant="cyan"
              clickable
              onClick={() => setKpiModalMetric("toilet_functional")}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 12h8" />
                <path d="m12 8 4 4-4 4" />
              </svg>
            </KpiCard>
            <KpiCard
              label="Water Coverage"
              value={pct(waterCoverage)}
              hint="Available coverage"
              iconVariant="purple"
              clickable
              onClick={() => setKpiModalMetric("water_coverage")}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2s7 8 7 13a7 7 0 0 1-14 0c0-5 7-13 7-13Z" />
              </svg>
            </KpiCard>
            <KpiCard
              label="Electricity"
              value={pct(electricityPct)}
              hint="Connected schools"
              iconVariant="orange"
              clickable
              onClick={() => setKpiModalMetric("electricity")}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" />
              </svg>
            </KpiCard>
            <KpiCard
              label="Weak Blocks"
              value={weakBlockCount}
              hint="Need monitoring"
              iconVariant="orange"
              clickable={weakBlockCount > 0}
              onClick={() => weakBlockCount > 0 && setWeakModalOpen(true)}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3v18h18" />
                <path d="m19 9-5 5-4-4-3 3" />
              </svg>
            </KpiCard>
          </div>
        </div>
      </section>

      <div className="ed-section-title">
        <div>
          <h2>Infrastructure Diagnostics</h2>
          <p>Breakdown and water metrics are separated into readable panels with larger spacing and action-oriented labels.</p>
        </div>
        <button type="button" className="ed-section-action" onClick={() => scrollTo("ed-infra-actions")}>
          View action queue →
        </button>
      </div>

      <section className="ed-infra-diagnostic-grid">
        <article className="ed-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Infrastructure Overview Breakdown</h3>
              <p>Weighted view of classroom, toilet, water and electricity readiness.</p>
            </div>
            <span className="ed-pill">Composite weight</span>
          </div>
          <div className="ed-infra-gauge-grid">
            {breakdown.length ? (
              breakdown.map((item) => (
                <MetricDonut
                  key={item.metric}
                  value={item.value}
                  label={item.metric}
                  subtitle={`Weight: ${item.weight}%`}
                  variant={DONUT_VARIANT[item.metric] || ""}
                />
              ))
            ) : (
              <>
                <MetricDonut value={classroomHealth} label="Classroom Health" subtitle="Weight: 30%" />
                <MetricDonut value={toiletFunctional} label="Toilet Functional" subtitle="Weight: 25%" />
                <MetricDonut value={waterCoverage} label="Water Availability" subtitle="Weight: 25%" variant="blue" />
                <MetricDonut value={electricityPct} label="Electricity" subtitle="Weight: 20%" variant="purple" />
              </>
            )}
          </div>
        </article>

        <article className="ed-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Water Safety Metrics</h3>
              <p>Coverage by source and verification type. Rainwater and purified water need follow-up.</p>
            </div>
            <span className="ed-pill">Water view</span>
          </div>
          <div className="ed-infra-water-chart" aria-label="Water safety metrics">
            {waterMetrics.map((item) => (
              <div className="ed-infra-water-bar" key={item.label}>
                <strong>{pct(item.value)}</strong>
                <div
                  className="ed-infra-bar-vertical"
                  style={{ height: `${Math.max((item.value / 100) * maxWaterBar, 8)}px` }}
                />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="ed-section-title" id="ed-infra-actions">
        <div>
          <h2>Insights and Next Actions</h2>
          <p>Infrastructure findings are converted into prioritised actions so district officers can move from diagnosis to execution.</p>
        </div>
        <button type="button" className="ed-section-action" onClick={() => scrollTo("ed-infra-table")}>
          View block table →
        </button>
      </div>

      <section className="ed-infra-lower-grid">
        <article className="ed-infra-insights-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Infrastructure Signals</h3>
              <p>Summarized from classrooms, toilet functionality, water availability and block-level infra readiness.</p>
            </div>
            <span className="ed-pill">Rule-based</span>
          </div>
          <div className="ed-infra-insight-stack">
            <article className="ed-infra-insight-card">
              <span className="ed-icon-box green">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m3 17 6-6 4 4 8-8" />
                  <path d="M14 7h7v7" />
                </svg>
              </span>
              <div>
                <h4>{infraIndex >= 85 ? "Infrastructure overview is strong" : "Infrastructure overview needs improvement"}</h4>
                <p>
                  {fmt(classroom.total_classrooms)} classrooms with classroom health at {pct(classroomHealth)} and toilet functionality at{" "}
                  {pct(toiletFunctional)}. District infra overview is {pct(infraIndex)}.
                </p>
              </div>
              <span className={`ed-infra-priority ${infraIndex >= 85 ? "good" : "high"}`}>
                {infraIndex >= 85 ? "Strong" : "Watch"}
              </span>
            </article>
            <article className="ed-infra-insight-card">
              <span className="ed-icon-box cyan">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2s7 8 7 13a7 7 0 0 1-14 0c0-5 7-13 7-13Z" />
                </svg>
              </span>
              <div>
                <h4>Water coverage is the main gap</h4>
                <p>
                  Water coverage is {pct(waterCoverage)}. Purified water is {pct(purifiedPct)} and rainwater support is only {pct(rainwaterPct)}.
                </p>
              </div>
              <span className="ed-infra-priority">Medium</span>
            </article>
            <article className="ed-infra-insight-card">
              <span className="ed-icon-box orange">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                </svg>
              </span>
              <div>
                <h4>Weak infrastructure blocks need field verification</h4>
                <p>{weakBlockNames} are flagged as the weakest infrastructure blocks for ground inspection.</p>
              </div>
              <span className="ed-infra-priority high">High</span>
            </article>
            <article className="ed-infra-insight-card">
              <span className="ed-icon-box purple">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" />
                </svg>
              </span>
              <div>
                <h4>{electricityPct >= 90 ? "Electricity can be treated as stable" : "Electricity coverage needs review"}</h4>
                <p>
                  Electricity coverage is at {pct(electricityPct)}, so immediate attention should shift to water safety and facility maintenance.
                </p>
              </div>
              <span className="ed-infra-priority good">Stable</span>
            </article>
          </div>
        </article>

        <aside className="ed-infra-action-panel">
          <h3>Recommended Action Queue</h3>
          <div className="ed-action-card">
            <article className="ed-action-item">
              <span className="ed-infra-small-icon red">1</span>
              <div>
                <h4>Inspect weakest infrastructure blocks</h4>
                <p>Start with {weakBlockNames}. Validate classroom, toilet and water records on ground.</p>
              </div>
            </article>
            <article className="ed-action-item">
              <span className="ed-infra-small-icon orange">2</span>
              <div>
                <h4>Improve purified water availability</h4>
                <p>Target schools below district average and schedule vendor/service follow-up for purification units.</p>
              </div>
            </article>
            <article className="ed-action-item">
              <span className="ed-infra-small-icon green">3</span>
              <div>
                <h4>Maintain strong classroom and toilet performance</h4>
                <p>Move high-scoring schools to a monthly exception-based review instead of weekly inspection.</p>
              </div>
            </article>
          </div>
          <button type="button" className="ed-infra-learn-more" onClick={() => scrollTo("ed-infra-table")}>
            <span>Open infra action tracker</span>
            <span>→</span>
          </button>
        </aside>
      </section>

      <section className="ed-panel ed-table-card" id="ed-infra-table">
        <div className="ed-table-head">
          <div>
            <h3>Block-Wise Infrastructure Summary</h3>
            <p>Detailed block table for classroom health, toilets, water, electricity and intervention status.</p>
          </div>
          <div className="ed-map-table-tools">
            <button
              type="button"
              className="ed-map-filter-chip"
              onClick={() => setPriorityOnly((v) => !v)}
            >
              {priorityOnly ? "Show all ▾" : "High priority only ▾"}
            </button>
          </div>
        </div>
        <table className="ed-table" aria-label="Block-wise infrastructure summary">
          <thead>
            <tr>
              <th>Block</th>
              <th>Classrooms</th>
              <th>Classroom Health</th>
              <th>Toilet Functional</th>
              <th>Water Coverage</th>
              <th>Electricity</th>
              <th>Infra Overview</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.length ? (
              tableRows.map((row) => (
                <tr key={row.block_code || row.block_name}>
                  <td>
                    {row.block_name ? (
                      <BlockLink blockCode={row.block_code} blockName={row.block_name}>
                        {row.block_name}
                      </BlockLink>
                    ) : (
                      row.block_name
                    )}
                  </td>
                  <td>{fmt(row.classrooms)}</td>
                  <td>{pct(row.classroom_health)}</td>
                  <td>{pct(row.toilet_pct)}</td>
                  <td>{pct(row.water_coverage_pct)}</td>
                  <td>{pct(row.electricity_pct)}</td>
                  <td className="ed-score">{row.infra_index}</td>
                  <td>
                    <span className={`ed-tag ${row.priority.className}`}>{row.priority.label}</span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>No block infrastructure data available for {scopeLabel}.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <InfrastructureWeakSchoolsModal
        open={weakModalOpen}
        onClose={() => setWeakModalOpen(false)}
        scopeLabel={scopeLabel}
        weakBlockCount={weakBlockCount}
      />

      <InfrastructureKpiSchoolsModal
        open={Boolean(kpiModalMetric)}
        metric={kpiModalMetric}
        onClose={() => setKpiModalMetric(null)}
        scopeLabel={scopeLabel}
        districtPct={
          kpiModalMetric === "classroom_health"
            ? classroomHealth
            : kpiModalMetric === "toilet_functional"
              ? toiletFunctional
              : kpiModalMetric === "water_coverage"
                ? waterCoverage
                : kpiModalMetric === "electricity"
                  ? electricityPct
                  : null
        }
      />
    </div>
  );
};

export default ExecutiveInfrastructureTab;
